import { DateUtils, ELanguage } from '@web_opxp/features';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function formatDate(unixTimestamp: number, locale: string = ELanguage.German, timezone?: string) {
  // timestamp === -1 means 'now' when sent to backend
  const date = DateUtils.unixTimestampToDate(unixTimestamp);
  return DateUtils.formatDate(
    date,
    {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    },
    locale
  );
}

export function printDayMonth(timestamp: number, locale: string) {
  const date = DateUtils.unixTimestampToDate(timestamp);
  return DateUtils.formatDate(
    date,
    {
      month: '2-digit',
      day: '2-digit',
    },
    locale
  );
}

export function isTimestampInDay(timestamp: number, date: Date): boolean {
  // Get the start and end timestamps of the day
  const startOfDayTimestamp = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const endOfDayTimestamp = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).getTime() - 1;

  // Check if the timestamp is within the day
  return timestamp >= startOfDayTimestamp && timestamp <= endOfDayTimestamp;
}

export function printShortMonth(timestamp: number, locale: string) {
  // timestamp === -1 means 'now' when sent to backend
  const date = DateUtils.unixTimestampToDate(timestamp);
  return DateUtils.formatDate(
    date,
    {
      month: 'short',
    },
    locale
  );
}

/** 0-based amount of days passed from given timestamp */
export function getDayOfWeekFromNow(unixTimestamp: number) {
  const today = new Date(Date.now());
  today.setUTCHours(0, 0, 0, 0);

  const startDay = DateUtils.unixTimestampToDate(unixTimestamp);
  startDay.setUTCHours(0, 0, 0, 0);

  const diffInMs = today.getTime() - startDay.getTime();
  const diffInDays = diffInMs / MS_PER_DAY;
  // day number
  return Math.floor(diffInDays);
}

export function isDayInPastSevenDays(timestamp: number) {
  if (timestamp < 0) {
    // timestamp = -1 means it was just done and even not saved
    return true;
  }
  const today = DateUtils.getUnixTimestamp(Date.now());

  const dateInPast = new Date(Date.now());
  dateInPast.setDate(dateInPast.getDate() - 7);

  return isDateBetweenTwoDates(DateUtils.getUnixTimestamp(dateInPast.getTime()), today, timestamp);
}

export function isDateBetweenTwoDates(startDate: number, endDate: number, date: number) {
  const isAfterStartDate = startDate < date;
  const isBeforeEndDate = date < endDate;

  return isAfterStartDate && isBeforeEndDate;
}

export function isDateToday(unixTimestamp: number) {
  if (unixTimestamp < 0) {
    // timestamp = -1 means it was just done and even not saved
    return true;
  }
  const today = new Date(Date.now()).setUTCHours(0, 0, 0, 0);
  const timestampDate = DateUtils.unixTimestampToDate(unixTimestamp).setUTCHours(0, 0, 0, 0);
  return today === timestampDate;
}

export function getDayFromToday(numberOfDays: number, locale: string) {
  const date = new Date(new Date().getTime() + numberOfDays * MS_PER_DAY);
  return DateUtils.formatDate(date, {
    weekday: 'long',
  });
}

// date received from backend/chargebee has a space instead of T character
// which is needed according to EcmaScript/ISO standart
// so we need to convert this date to supported format
const CETDateStringRegex = /(\d{4}-[01]\d-[0-3]\d)\s([0-2]\d:[0-5]\d:[0-5]\d)/g;

export function convertFromCETStringToUTCTimestamp(cetStringDate: string) {
  try {
    const normalizedDateTimeString = cetStringDate.replace(CETDateStringRegex, '$1T$2.0Z');
    const date = new Date(normalizedDateTimeString);
    return DateUtils.getUnixTimestamp(date.valueOf());
  } catch (error) {
    console.error(error);
    return 0;
  }
}

export function getMonday(d: string | number | Date) {
  const date = new Date(d);
  const weekDay = date.getDay();
  const diff = date.getDate() - weekDay + (weekDay === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
}

export function parseDateString(dateStr: string) {
  try {
    const [day, month, year] = dateStr.split('/');
    return new Date(`${year}-${month}-${day}`);
  } catch {
    console.error('Invalid date format: ', dateStr);
    // ignore errors for now
  }
}
